/**
 * Notification System DTOs
 *
 * Data transfer objects for notification management
 */

import { z } from 'zod';
import {
  DeviceType,
  DigestFrequency,
  NotificationChannel,
  NotificationEvent,
  NotificationSeverity,
  NotificationStatus,
  NotificationType,
  RecipientType,
} from '../enums/notification.enums';

// ========================================
// NOTIFICATION DTOs
// ========================================

export const NotificationSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  userId: z.string().uuid().optional(),

  type: z.nativeEnum(NotificationType),
  channel: z.nativeEnum(NotificationChannel),
  title: z.string(),
  message: z.string(),
  actionUrl: z.string().url().optional(),
  imageUrl: z.string().url().optional(),

  event: z.nativeEnum(NotificationEvent),
  severity: z.nativeEnum(NotificationSeverity),
  data: z.record(z.string(), z.unknown()).optional(),

  fcmToken: z.string().optional(),
  fcmMessageId: z.string().optional(),

  status: z.nativeEnum(NotificationStatus),
  sentAt: z.date().optional(),
  deliveredAt: z.date().optional(),
  readAt: z.date().optional(),
  failedAt: z.date().optional(),
  attempts: z.number().int().min(0),
  lastError: z.string().optional(),

  createdAt: z.date(),
  updatedAt: z.date(),
  expiresAt: z.date().optional(),
});

export type Notification = z.infer<typeof NotificationSchema>;

export const CreateNotificationSchema = z.object({
  businessId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  type: z.nativeEnum(NotificationType),
  channel: z.nativeEnum(NotificationChannel),
  title: z.string().min(1).max(255),
  message: z.string().min(1),
  actionUrl: z.string().url().optional(),
  imageUrl: z.string().url().optional(),
  event: z.nativeEnum(NotificationEvent),
  severity: z.nativeEnum(NotificationSeverity).default(NotificationSeverity.INFO),
  data: z.record(z.string(), z.unknown()).optional(),
  expiresAt: z.date().optional(),
});

export type CreateNotification = z.infer<typeof CreateNotificationSchema>;

export const UpdateNotificationSchema = z.object({
  status: z.nativeEnum(NotificationStatus).optional(),
  readAt: z.date().optional(),
});

export type UpdateNotification = z.infer<typeof UpdateNotificationSchema>;

// ========================================
// NOTIFICATION RULE DTOs
// ========================================

export const NotificationRuleSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().optional(),
  active: z.boolean(),

  event: z.nativeEnum(NotificationEvent),
  conditions: z.record(z.string(), z.unknown()).optional(),

  recipientType: z.nativeEnum(RecipientType),
  recipientIds: z.array(z.string().uuid()).optional(),
  roles: z.array(z.string()).optional(),

  channels: z.array(z.nativeEnum(NotificationChannel)),
  templateId: z.string().uuid().optional(),
  throttle: z.record(z.string(), z.unknown()).optional(),
  priority: z.number().int().min(0).max(100),

  createdAt: z.date(),
  updatedAt: z.date(),
});

export type NotificationRule = z.infer<typeof NotificationRuleSchema>;

export const CreateRuleSchema = z.object({
  businessId: z.string().uuid().optional(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  active: z.boolean().default(true),

  event: z.nativeEnum(NotificationEvent),
  conditions: z.record(z.string(), z.unknown()).optional(),

  recipientType: z.nativeEnum(RecipientType),
  recipientIds: z.array(z.string().uuid()).optional(),
  roles: z.array(z.string()).optional(),

  channels: z.array(z.nativeEnum(NotificationChannel)).min(1),
  templateId: z.string().uuid().optional(),
  throttle: z
    .object({
      maxPerHour: z.number().int().min(1).optional(),
      maxPerDay: z.number().int().min(1).optional(),
      deduplicateWindow: z.number().int().min(0).optional(), // seconds
    })
    .optional(),
  priority: z.number().int().min(0).max(100).default(0),
});

export type CreateRule = z.infer<typeof CreateRuleSchema>;

export const UpdateRuleSchema = CreateRuleSchema.partial();
export type UpdateRule = z.infer<typeof UpdateRuleSchema>;

// ========================================
// NOTIFICATION PREFERENCE DTOs
// ========================================

export const NotificationPreferenceSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),

  emailEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  inAppEnabled: z.boolean(),

  eventPreferences: z.record(z.string(), z.boolean()).optional(),

  quietHoursStart: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(), // "22:00"
  quietHoursEnd: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(), // "08:00"
  timezone: z.string(),
  digestMode: z.boolean(),
  digestFrequency: z.nativeEnum(DigestFrequency).optional(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

export const UpdatePreferenceSchema = z.object({
  emailEnabled: z.boolean().optional(),
  smsEnabled: z.boolean().optional(),
  pushEnabled: z.boolean().optional(),
  inAppEnabled: z.boolean().optional(),

  eventPreferences: z.record(z.string(), z.boolean()).optional(),

  quietHoursStart: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  quietHoursEnd: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  timezone: z.string().optional(),
  digestMode: z.boolean().optional(),
  digestFrequency: z.nativeEnum(DigestFrequency).optional(),
});

export type UpdatePreference = z.infer<typeof UpdatePreferenceSchema>;

// ========================================
// NOTIFICATION TEMPLATE DTOs
// ========================================

export const NotificationTemplateSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid().optional(),

  name: z.string(),
  description: z.string().optional(),
  event: z.nativeEnum(NotificationEvent),
  channel: z.nativeEnum(NotificationChannel),

  subject: z.string().optional(),
  body: z.string(),
  htmlBody: z.string().optional(),
  variables: z.record(z.string(), z.string()).optional(),

  fromName: z.string().optional(),
  fromEmail: z.string().email().optional(),

  icon: z.string().url().optional(),
  badge: z.string().optional(),
  sound: z.string().optional(),
  clickAction: z.string().url().optional(),

  active: z.boolean(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

export type NotificationTemplate = z.infer<typeof NotificationTemplateSchema>;

export const CreateTemplateSchema = z.object({
  businessId: z.string().uuid().optional(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  event: z.nativeEnum(NotificationEvent),
  channel: z.nativeEnum(NotificationChannel),

  subject: z.string().max(255).optional(),
  body: z.string().min(1),
  htmlBody: z.string().optional(),
  variables: z.record(z.string(), z.string()).optional(),

  fromName: z.string().max(255).optional(),
  fromEmail: z.string().email().optional(),

  icon: z.string().url().optional(),
  badge: z.string().optional(),
  sound: z.string().optional(),
  clickAction: z.string().url().optional(),

  active: z.boolean().default(true),
});

export type CreateTemplate = z.infer<typeof CreateTemplateSchema>;

export const UpdateTemplateSchema = CreateTemplateSchema.partial();
export type UpdateTemplate = z.infer<typeof UpdateTemplateSchema>;

// ========================================
// FCM TOKEN DTOs
// ========================================

export const FcmTokenSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  token: z.string(),
  deviceType: z.nativeEnum(DeviceType),
  deviceName: z.string().optional(),
  isActive: z.boolean(),
  lastUsedAt: z.date().optional(),
  expiresAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type FcmToken = z.infer<typeof FcmTokenSchema>;

export const RegisterFcmTokenSchema = z.object({
  token: z.string().min(1),
  deviceType: z.nativeEnum(DeviceType),
  deviceName: z.string().max(255).optional(),
});

export type RegisterFcmToken = z.infer<typeof RegisterFcmTokenSchema>;

// ========================================
// EVENT PAYLOAD DTOs
// ========================================

/**
 * Event payload for emitting notification events
 * Used internally by EventEmitter2
 */
export const NotificationEventPayloadSchema = z.object({
  event: z.nativeEnum(NotificationEvent),
  businessId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  severity: z.nativeEnum(NotificationSeverity).default(NotificationSeverity.INFO),
  data: z.record(z.string(), z.unknown()).optional(),
  metadata: z
    .object({
      triggeredBy: z.string().optional(),
      source: z.string().optional(),
      correlationId: z.string().uuid().optional(),
    })
    .optional(),
});

export type NotificationEventPayload = z.infer<typeof NotificationEventPayloadSchema>;

// ========================================
// QUERY/FILTER DTOs
// ========================================

export const NotificationsFilterSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  userId: z.string().uuid().optional(),
  event: z.nativeEnum(NotificationEvent).optional(),
  channel: z.nativeEnum(NotificationChannel).optional(),
  status: z.nativeEnum(NotificationStatus).optional(),
  severity: z.nativeEnum(NotificationSeverity).optional(),
  type: z.nativeEnum(NotificationType).optional(),
  unreadOnly: z.boolean().optional(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'sentAt']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type NotificationsFilter = z.infer<typeof NotificationsFilterSchema>;

export const RulesFilterSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  event: z.nativeEnum(NotificationEvent).optional(),
  active: z.boolean().optional(),
  search: z.string().optional(),
  sortBy: z.enum(['name', 'priority', 'createdAt']).default('priority'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type RulesFilter = z.infer<typeof RulesFilterSchema>;
